import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import dayjs from 'dayjs';

import Yup from '../../../../utils/yup';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import useMenus from '../../../../context/MenuContext';
import useUsers from '../../../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase, DeleteDialogUI, ButtonUI } from '../../../Interface';
import ConformityReports from './ConformityReports';
import ConformityFields from './ConformityFields';
import conformityService from '../../../../service/client/varadiMetal/conformity.service';
import { cleanValues, generateAutocompleteItems } from '../../../../utils/helper';
import masterDataService from '../../../../service/masterData.service';
import { useLoader } from '../../../../provider/LoaderProvider';
import ConformityTabs from './ConformityTabs';
import useConfig from '../../../../context/ConfigContext';

const ConformityPage = () => {
  const { showLoader, hideLoader } = useLoader();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const { getConfig, config } = useConfig();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    company: [],
    items: [],
  });
  const [autocompleteData, setAutoCompleteData] = useState({});
  const [submenu, setSubmenu] = useState(null);

  useEffect(() => {
    getConfig();
  }, []);

  useEffect(() => {
    if (Object.keys(config).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          supplier: config?.name ?? null,
          issuePlace: config?.city ?? null,
          standard: config?.conformityStandard ?? null,
        };
      });
    }
  }, [config]);

  const def = {
    id: null,
    standard: '',
    companyId: null,
    supplier: '',
    orderNo: '',
    deliveryNo: '',
    deliveryDate: dayjs(),
    certificateNo: '',
    measureDate: dayjs(),
    classification: 'MEGFELELT',
    comment: '',
    issuePlace: '',
    issueDate: dayjs(),
    itemCode: '',
    itemName: '',
    itemDescription: '',
    qtyLabel: '',
    baseId: null,
    baseLine: null,
    baseDocNum: '',
    conformityComponent: [],
  };

  const defConformityComponent = {
    id: null,
    stringAttr1: '',
    stringAttr2: '',
    stringAttr3: '',
    stringAttr4: '',
    numberAttr1: '',
    numberAttr2: '',
    numberAttr3: '',
    boolAttr1: false,
    boolAttr2: false,
    isNew: true,
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .itemsFromTableToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    showLoader();

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          company: resp[0],
          items: resp[1],
        };

        setAutoCompleteData(aData);

        const acOptions = {
          company: generateAutocompleteItems(aData.company, ['code', 'name'], 'id'),
          items: generateAutocompleteItems(aData.items, ['itemCode', 'itemName'], 'itemCode'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
      });
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    setDefaultValues(def);
    setDefaultSelectedValues(def);

    initDefaultValues();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('varadiMetal/conformity');
    setSubmenu(submenuId);
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    standard: Yup.string().required(),
    companyId: Yup.number().required(),
    supplier: Yup.string().nullable(),
    orderNo: Yup.string().nullable(),
    deliveryNo: Yup.string().nullable(),
    certificateNo: Yup.string().nullable(),
    measureDate: Yup.date().nullable(),
    classification: Yup.string().nullable(),
    comment: Yup.string().nullable(),
    issuePlace: Yup.string().nullable(),
    issueDate: Yup.date().nullable(),
    itemCode: Yup.string().nullable(),
    itemName: Yup.string().nullable(),
    itemDescription: Yup.string().nullable(),
    qtyLabel: Yup.string().nullable(),
    baseId: Yup.number().required(),
    baseLine: Yup.number().nullable(),
    baseDocNum: Yup.string().nullable(),
    deliveryDate: Yup.date().nullable(),
    conformityComponent: Yup.array().of(
      Yup.object().shape({
        stringAttr1: Yup.string().required(),
        stringAttr2: Yup.string().nullable(),
        stringAttr3: Yup.string().nullable(),
        stringAttr4: Yup.string().nullable(),
        numberAttr1: Yup.number().nullable(),
        numberAttr2: Yup.number().nullable(),
        numberAttr3: Yup.number().nullable(),
        boolAttr1: Yup.boolean().nullable(),
        boolAttr2: Yup.boolean().nullable(),
      })
    ),
  });

  const valuesToForm = () => {
    const {
      id,
      standard,
      companyId,
      supplier,
      orderNo,
      deliveryNo,
      deliveryDate,
      certificateNo,
      measureDate,
      classification,
      comment,
      issuePlace,
      issueDate,
      itemCode,
      itemName,
      itemDescription,
      qtyLabel,
      baseId,
      baseLine,
      baseDocNum,
      conformityComponent,
    } = selectedValues;

    setInitValues({
      id,
      standard: standard || '',
      companyId,
      supplier: supplier || '',
      orderNo: orderNo || '',
      deliveryNo: deliveryNo || '',
      deliveryDate: deliveryDate ? dayjs(deliveryDate) : null,
      certificateNo: certificateNo || '',
      measureDate: measureDate ? dayjs(measureDate) : null,
      classification: classification || '',
      comment: comment || '',
      issuePlace: issuePlace || '',
      issueDate: issueDate ? dayjs(issueDate) : null,
      itemCode,
      itemName,
      itemDescription: itemDescription || '',
      qtyLabel: qtyLabel || '',
      baseId,
      baseLine,
      baseDocNum,
      conformityComponent:
        conformityComponent?.map((cp) => ({
          id: cp.id,
          conformityId: cp.conformityId,
          stringAttr1: cp.stringAttr1 || '',
          stringAttr2: cp.stringAttr2 || '',
          stringAttr3: cp.stringAttr3 || '',
          stringAttr4: cp.stringAttr4 || '',
          numberAttr1: cp.numberAttr1 || '',
          numberAttr2: cp.numberAttr2 || '',
          numberAttr3: cp.numberAttr3 || '',
          boolAttr1: cp.boolAttr1 || false,
          boolAttr2: cp.boolAttr2 || false,
          isNew: false,
        })) ?? [],
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...cleanValues(values),
      conformityComponent: values.conformityComponent.map((cp) => ({
        ...cleanValues(cp),
        id: cp.isNew ? undefined : cp.id,
      })),
    };

    conformityService.updateItem(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...cleanValues(values),
      conformityComponent: values.conformityComponent.map((cp) => ({
        ...cleanValues(cp),
        id: cp.isNew ? undefined : cp.id,
      })),
    };

    conformityService
      .createItem(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    conformityService.deleteData(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  const printClick = () => {
    addNotification({
      content: 'Nyomtatás elindult!',
      type: NotificationType.INFO,
    });

    conformityService.printDocument({
      docId: selectedValues.id,
    });
  };

  return (
    <>
      <FormBase
        name="Conformity"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <div className="flex items-center flex-wrap justify-between">
                <div className="flex items-center flex-wrap">
                  <FormHeaderUI
                    addClick={() => {
                      setSelectedValues(defaultSelectedValues);
                      setViewField(false);
                      setAddField(false);
                    }}
                    addDisabled={!viewField || !canEdit}
                    modClick={() => {
                      setViewField(false);
                      setEditButtonvisible(false);
                    }}
                    modDisabled={!viewField || operatorsDisabled || !canEdit}
                    deleteClick={() => {
                      setShowDeleteDialog(true);
                    }}
                    deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                    copyClick={() => {
                      setViewField(false);
                      setAddField(false);
                    }}
                    copyDisabled={!viewField || operatorsDisabled || !canEdit}
                  />
                  <ButtonUI
                    text="Nyomtatás"
                    size="sm"
                    className="bg-labelColor py-3"
                    onClick={() => printClick()}
                    disabled={!viewField || operatorsDisabled}
                  />
                </div>
              </div>

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <ConformityFields
                      viewField={viewField}
                      autocompleteOptions={autocompleteOptions}
                      autocompleteData={autocompleteData}
                    />

                    <ConformityTabs
                      submenuId={submenu}
                      selectedValues={selectedValues}
                      setDefaultValues={setDefaultValues}
                      viewField={viewField}
                      autocompleteOptions={autocompleteOptions}
                      autocompleteData={autocompleteData}
                      disabled={viewField}
                      defConformityComponent={defConformityComponent}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <ConformityReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autocompleteOptions={autocompleteOptions}
            autocompleteData={autocompleteData}
            submenuId={submenu}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

export default ConformityPage;
